// src/pages/cafe/CafeExpenseLedger.jsx
// ✅ 카페 지점별 지출 기안 대장 — 지점 × 항목마다 그 달 기안을 작성했는지 한눈에.
//    기안번호가 없는 칸은 옅은 경고색으로 남아 "누락"이 그대로 보인다.
//
// ⚠️ 지점·항목 목록은 이 파일에 없다. 백엔드 constants.py가 유일한 원본이고
//    /cafe-expense-ledger/config/로 받아서 그린다(두 곳에 두면 언젠가 어긋난다).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../api";

const MONTH_COUNT = 3;   // 한 화면에 나란히 보여줄 개월 수

const S = {
  wrap: { padding: 16, fontSize: 13, color: "#1f2937" },
  bar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16 },
  btn: (c, d) => ({
    width: "auto", flex: "0 0 auto", height: 34, padding: "0 14px", background: d ? "#9ca3af" : c,
    color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 13,
    cursor: d ? "not-allowed" : "pointer", whiteSpace: "nowrap",
  }),
  navBtn: {
    width: 34, height: 34, flex: "0 0 auto", border: "1px solid #d1d5db", borderRadius: 6,
    background: "#fff", color: "#374151", cursor: "pointer", fontSize: 14, padding: 0,
  },
  section: {
    background: "#fff", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    padding: 16, marginBottom: 16,
  },
  secHead: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  secTitle: { margin: 0, fontSize: 16, fontWeight: 700, whiteSpace: "nowrap" },
  th: {
    background: "#f1f5f9", padding: "6px 8px", borderBottom: "1px solid #cbd5e1",
    whiteSpace: "nowrap", fontSize: 12, fontWeight: 700,
  },
  thMonth: {
    background: "#e0e7ff", padding: "6px 8px", borderBottom: "1px solid #cbd5e1",
    whiteSpace: "nowrap", fontSize: 12, fontWeight: 700, textAlign: "center", color: "#3730a3",
  },
  td: { padding: "4px 6px", borderBottom: "1px solid #eef2f7", verticalAlign: "top" },
  tdItem: {
    padding: "4px 8px", borderBottom: "1px solid #eef2f7", whiteSpace: "nowrap",
    fontWeight: 600, background: "#fafafa",
  },
  // ✅ 기안번호가 없는 칸 — 누락이 보이게 옅은 경고색
  missing: { background: "#fffbeb" },
  inp: {
    width: "100%", padding: "3px 5px", borderRadius: 4, border: "1px solid #cbd5e1",
    boxSizing: "border-box", fontSize: 12,
  },
  // ⚠️ tabular-nums — 비례폭 숫자는 자릿수마다 폭이 달라 "111,111,111"과
  // "000,000,000"의 실제 너비가 다르다. 고정폭 숫자로 맞춰야 열 폭을 자릿수
  // 기준으로 잡을 수 있다.
  inpR: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  // 항목 참고사항 — 월과 무관한 메모라 달 열 바깥(표 오른쪽 끝) 한 칸.
  tdNote: { padding: "4px 6px", borderBottom: "1px solid #eef2f7", verticalAlign: "top" },
  tag: {
    display: "inline-flex", alignItems: "center", gap: 2, whiteSpace: "nowrap",
    background: "#eef2ff", color: "#3730a3", borderRadius: 10, padding: "1px 2px 1px 6px",
    fontSize: 11, marginRight: 3, marginBottom: 3, maxWidth: "100%",
  },
  tagX: {
    width: 16, height: 16, flex: "0 0 auto", border: "none", borderRadius: "50%",
    background: "transparent", color: "#3730a3", cursor: "pointer", fontSize: 11,
    lineHeight: 1, padding: 0,
  },
  addBtn: {
    width: 22, height: 22, flex: "0 0 auto", border: "1px solid #93c5fd", borderRadius: 4,
    background: "#eff6ff", color: "#2563eb", cursor: "pointer", fontSize: 12, lineHeight: 1, padding: 0,
  },
};

const fmt = (n) => Number(n || 0).toLocaleString();
const num = (v) => {
  const n = Number(String(v ?? "0").replace(/[, 원]/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const ymLabel = (ym) => `${ym.slice(0, 4)}년 ${Number(ym.slice(5, 7))}월`;

// 기준월에서 offset만큼 이동한 "YYYY-MM"
const shiftYm = (ym, offset) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const thisYm = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const cellKey = (ym, branch, item) => `${ym}|${branch}|${item}`;

export default function CafeExpenseLedger() {
  const [config, setConfig] = useState(null);
  // endYm = 화면 오른쪽 끝 달. 기본은 이번 달이라 "최근 3개월"이 기본으로 보인다.
  const [endYm, setEndYm] = useState(thisYm());
  const [cells, setCells] = useState({});     // key -> {amount, draft_nos, note}
  const [busy, setBusy] = useState(false);
  const [savingBranch, setSavingBranch] = useState(null);
  const [msg, setMsg] = useState("");
  const [draftInput, setDraftInput] = useState({});   // key -> 입력 중인 기안번호
  // 항목 참고사항 — "지점|항목" -> 문자열. 월 축이 없어 cells와 별도로 둔다.
  const [itemNotes, setItemNotes] = useState({});

  const months = useMemo(
    () => Array.from({ length: MONTH_COUNT }, (_, i) => shiftYm(endYm, i - (MONTH_COUNT - 1))),
    [endYm]
  );

  // ⚠️ document.title만 세운다 — 탭 제목은 AppLayout의 TITLE_MAP이 경로로 정한다.
  // 페이지가 navigate로 제목을 실어 보내면 목록이 대시보드로 튕기는 문제가 있었다.
  useEffect(() => {
    document.title = "카페 지출 기안 대장";
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cafe-expense-ledger/config/");
        setConfig(data);
        // 참고사항은 구성과 함께 온다(월 무관이라 월별 조회에 태우지 않는다).
        const notes = {};
        (data.branches || []).forEach((b) =>
          (b.items || []).forEach((it) => { notes[`${b.code}|${it.code}`] = it.note || ""; })
        );
        setItemNotes(notes);
      } catch (err) {
        console.error("❌ 구성 조회 실패:", err);
        setMsg("지점·항목 구성을 불러오지 못했습니다.");
      }
    })();
  }, []);

  const loadRows = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/cafe-expense-ledger/", {
        params: { months: months.join(",") },
      });
      const next = {};
      (data.rows || []).forEach((r) => {
        next[cellKey(r.year_month, r.branch, r.item)] = {
          amount: r.amount, draft_nos: r.draft_nos || [], note: r.note || "",
        };
      });
      setCells(next);
      setMsg("");
    } catch (err) {
      console.error("❌ 대장 조회 실패:", err);
      setMsg("조회에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  }, [months]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const getCell = (ym, branch, item) =>
    cells[cellKey(ym, branch, item)] || { amount: 0, draft_nos: [], note: "" };

  const setCell = (ym, branch, item, patch) =>
    setCells((p) => {
      const k = cellKey(ym, branch, item);
      return { ...p, [k]: { ...(p[k] || { amount: 0, draft_nos: [], note: "" }), ...patch } };
    });

  const addDraftNo = (ym, branch, item) => {
    const k = cellKey(ym, branch, item);
    const v = (draftInput[k] || "").trim();
    if (!v) return;
    const cur = getCell(ym, branch, item).draft_nos;
    if (cur.includes(v)) { setDraftInput((p) => ({ ...p, [k]: "" })); return; }
    setCell(ym, branch, item, { draft_nos: [...cur, v] });
    setDraftInput((p) => ({ ...p, [k]: "" }));
  };

  const removeDraftNo = (ym, branch, item, no) =>
    setCell(ym, branch, item, {
      draft_nos: getCell(ym, branch, item).draft_nos.filter((x) => x !== no),
    });

  const saveBranch = async (branch) => {
    const b = config?.branches.find((x) => x.code === branch);
    if (!b) return;
    setSavingBranch(branch);
    try {
      const rows = [];
      months.forEach((ym) =>
        b.items.forEach((it) => {
          const c = getCell(ym, branch, it.code);
          rows.push({
            year_month: ym, item: it.code,
            amount: num(c.amount), draft_nos: c.draft_nos, note: c.note,
          });
        })
      );
      const item_notes = {};
      b.items.forEach((it) => { item_notes[it.code] = itemNotes[`${branch}|${it.code}`] || ""; });
      const { data } = await api.post("/cafe-expense-ledger/save/",
        { branch, months, rows, item_notes });
      setMsg(`${b.label} 저장 완료 (${data.saved}칸 저장${data.removed ? `, ${data.removed}칸 비움` : ""}`
        + `${data.notes_saved ? `, 참고사항 ${data.notes_saved}건` : ""})`);
      await loadRows();
    } catch (err) {
      console.error("❌ 저장 실패:", err);
      setMsg(`저장 실패: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSavingBranch(null);
    }
  };

  // 그 지점 × 그 달에 기안번호가 비어 있는 항목 수 (누락 카운트)
  const missingCount = (branch, items, ym) =>
    items.filter((it) => getCell(ym, branch, it.code).draft_nos.length === 0).length;

  return (
    <div style={S.wrap}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>카페 지출 기안 대장</h2>
      <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 12 }}>
        지점별 월 정기 지출 항목의 기안 작성 여부 — 기안번호가 없는 칸은 노란 배경으로 남습니다.
      </div>

      <div style={S.bar}>
        <button style={S.navBtn} onClick={() => setEndYm(shiftYm(endYm, -1))} title="이전 달">◀</button>
        <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
          {ymLabel(months[0])} ~ {ymLabel(months[months.length - 1])}
        </span>
        <button style={S.navBtn} onClick={() => setEndYm(shiftYm(endYm, 1))} title="다음 달">▶</button>
        <button style={S.btn("#6b7280", false)} onClick={() => setEndYm(thisYm())}>최근 3개월</button>
        <button style={S.btn("#4a90e2", busy)} disabled={busy} onClick={loadRows}>
          {busy ? "불러오는 중…" : "새로고침"}
        </button>
        {msg && <span style={{ color: "#2563eb", fontSize: 12 }}>{msg}</span>}
      </div>

      {!config && <div style={{ color: "#9ca3af" }}>구성을 불러오는 중…</div>}

      {config?.branches.map((b) => (
        <div key={b.code} style={S.section}>
          <div style={S.secHead}>
            <h3 style={S.secTitle}>{b.label}</h3>
            <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
              항목 {b.items.length}종
            </span>
            {/* 달별 누락 건수 — 어느 달이 비어 있는지 표 안 보고도 바로 보이게 */}
            {months.map((ym) => {
              const miss = missingCount(b.code, b.items, ym);
              return (
                <span key={ym} style={{
                  fontSize: 12, whiteSpace: "nowrap", fontWeight: 600,
                  color: miss ? "#b45309" : "#16a34a",
                }}>
                  {ymLabel(ym)} {miss ? `미작성 ${miss}` : "완료"}
                </span>
              );
            })}
            <button
              style={{ ...S.btn("#16a34a", savingBranch === b.code), marginLeft: "auto" }}
              disabled={savingBranch === b.code}
              onClick={() => saveBranch(b.code)}
            >
              {savingBranch === b.code ? "저장 중…" : "저장"}
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", minWidth: 980, width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, minWidth: 140 }} rowSpan={2}>구분</th>
                  {months.map((ym) => (
                    <th key={ym} style={S.thMonth} colSpan={2}>{ymLabel(ym)}</th>
                  ))}
                  {/* 월 열 바깥 — 참고사항은 달이 바뀌어도 같은 값이다.
                      186px = 150 + 금액 열에서 줄인 12px × 3개월. 표 전체 폭(980)은
                      그대로 두고 남는 폭을 이쪽으로 옮긴 것이다. */}
                  <th style={{ ...S.th, minWidth: 186 }} rowSpan={2}>참고사항</th>
                </tr>
                <tr>
                  {months.map((ym) => (
                    <React.Fragment key={ym}>
                      {/* 금액 92px — 폰트 실측으로 잡은 하한이다(글자 수 어림이 아니다).
                          "999,999,999" = 숫자 9 + 콤마 2. 12px에서 실제 자간 폭:
                            Segoe UI  숫자 6.469 콤마 2.602 → 텍스트 63.42 → 필요 87.42
                            Arial     숫자 6.674 콤마 3.334 → 텍스트 66.73 → 필요 90.73
                            Tahoma    숫자 6.551 콤마 3.633 → 텍스트 66.22 → 필요 90.22
                          (필요 = 텍스트 + input 패딩10·테두리2 + td 패딩12 = 텍스트+24)
                          index.css의 폰트 스택이 Windows에서 Segoe UI로 풀리지만 대체
                          폰트까지 견디도록 최악값 90.73을 기준 삼아 91이 정수 최소,
                          서브픽셀 반올림 여유 1px을 더해 92로 잡았다. 80px은 약 11px
                          모자라 마지막 자리가 잘린다.
                          기안번호 180px = "260701-001 ✕" 태그 2개가 한 줄에 들어가는 폭. */}
                      <th style={{ ...S.th, minWidth: 92 }}>금액</th>
                      <th style={{ ...S.th, minWidth: 180 }}>기안번호</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.items.map((it) => (
                  <tr key={it.code}>
                    <td style={S.tdItem}>{it.label}</td>
                    {months.map((ym) => {
                      const c = getCell(ym, b.code, it.code);
                      const k = cellKey(ym, b.code, it.code);
                      const missing = c.draft_nos.length === 0;
                      return (
                        <React.Fragment key={ym}>
                          <td style={{ ...S.td, ...(missing ? S.missing : {}) }}>
                            <input
                              value={c.amount ? fmt(num(c.amount)) : ""}
                              onChange={(e) => setCell(ym, b.code, it.code, { amount: e.target.value })}
                              placeholder="0"
                              style={{ ...S.inp, ...S.inpR }}
                            />
                          </td>
                          <td style={{ ...S.td, ...(missing ? S.missing : {}) }}>
                            <div style={{ display: "flex", gap: 4 }}>
                              <input
                                value={draftInput[k] ?? ""}
                                onChange={(e) => setDraftInput((p) => ({ ...p, [k]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === "Enter") addDraftNo(ym, b.code, it.code); }}
                                placeholder="기안번호 입력"
                                style={S.inp}
                              />
                              <button
                                style={S.addBtn}
                                onClick={() => addDraftNo(ym, b.code, it.code)}
                                title="이 칸에 기안번호 추가"
                              >+</button>
                            </div>
                            {c.draft_nos.length > 0 && (
                              <div style={{ marginTop: 3 }}>
                                {c.draft_nos.map((no) => (
                                  <span key={no} style={S.tag} title={no}>
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{no}</span>
                                    <button
                                      style={S.tagX}
                                      onClick={() => removeDraftNo(ym, b.code, it.code, no)}
                                      title="삭제"
                                    >✕</button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td style={S.tdNote}>
                      <input
                        value={itemNotes[`${b.code}|${it.code}`] ?? ""}
                        onChange={(e) => setItemNotes((p) => ({
                          ...p, [`${b.code}|${it.code}`]: e.target.value,
                        }))}
                        placeholder="참고사항"
                        maxLength={200}
                        style={S.inp}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
